import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import type {
  CloudProcessing,
  RtmHosting,
  RtmServiceMetric,
  ServicesEnvelope,
} from '@/lib/mqtt/schemas/services';

export type RtmFilter = 'healthy' | 'degraded' | 'offline';

interface RtmStore {
  byName: Record<string, RtmServiceMetric>;
  /** Cluster-wide hosting snapshot — null until the services envelope
   *  supplies one (live mode stays null until backend ships it). */
  hosting: RtmHosting | null;
  /** Cloud-processing roll-up — drives the Agents tab "Apache Flink"
   *  card and any future RTM detail view. Null when live source is
   *  active and the backend hasn't shipped the field yet. */
  cloud: CloudProcessing | null;
  selectedName: string | null;
  detailWindow: '1m' | '5m' | '15m' | '60m' | '24h';
  filters: Record<RtmFilter, boolean>;
  apply: (env: ServicesEnvelope) => void;
  select: (name: string | null) => void;
  setWindow: (w: RtmStore['detailWindow']) => void;
  toggleFilter: (f: RtmFilter) => void;
}

export const useRtmStore = create<RtmStore>()(
  devtools(
    (set) => ({
      byName: {},
      hosting: null,
      cloud: null,
      selectedName: null,
      detailWindow: '5m',
      filters: { healthy: true, degraded: true, offline: false },
      apply: (env) => set(() => ({
        byName: Object.fromEntries(env.services.map((s) => [s.service_name, s])),
        hosting: env.hosting ?? null,
        cloud: env.cloud_processing ?? null,
      })),
      select: (name) => set({ selectedName: name }),
      setWindow: (w) => set({ detailWindow: w }),
      toggleFilter: (f) => set((s) => ({ filters: { ...s.filters, [f]: !s.filters[f] } })),
    }),
    { name: 'rtm' },
  ),
);
